import i18next from 'i18next';
import { useMemo } from 'react';

import { getNumberOfDaysBetween, currentDate } from '#utils/date';
import { ROUTES } from '#constants/routes';
import { BANNER_STATUS } from '#components/TopBanner/constants';
import { formatSystemsList } from '#components/TopBanner/utils';
import { TOP_BANNER_STATUS } from '#constants/topBanner';
import { SYSTEM_TYPES } from '#constants/legalSystems';
import { getFreeTrialEndDate } from '#models/workspace';

import { useSubscriptionStatus } from './useSubscriptionStatus';

const MONITORED_SYSTEMS = [SYSTEM_TYPES.PJN, SYSTEM_TYPES.MEV, SYSTEM_TYPES.EJE];

const getStatusProps = (status, daysRemaining) =>
  ({
    [TOP_BANNER_STATUS.NORMAL]: {
      hideBanner: true
    },
    [TOP_BANNER_STATUS.FREE_TRIAL]: {
      hideBanner: true
    },
    [TOP_BANNER_STATUS.SUBSCRIPTION_CANCELLED]: {
      hideBanner: true
    },
    [TOP_BANNER_STATUS.FREE_TRIAL_ENDING]: {
      bannerStatus: BANNER_STATUS.INFO,
      bannerTitle: i18next.t('TopBanner:freeTrialDaysRemaining', {
        daysRemaining,
        plural: daysRemaining > 1 ? 's' : ''
      }),
      bannerSubtitleKey: 'TopBanner:seeThePlans',
      bannerLink: ROUTES.SUBSCRIPTION_PLANS.path
    },
    [TOP_BANNER_STATUS.FREE_TRIAL_OVER]: {
      bannerStatus: BANNER_STATUS.ALERT,
      bannerTitle: i18next.t('TopBanner:freeTrialOver'),
      bannerSubtitleKey: 'TopBanner:seeThePlans',
      bannerLink: ROUTES.SUBSCRIPTION_PLANS.path
    },
    [TOP_BANNER_STATUS.SUBSCRIPTION_OVER]: {
      bannerStatus: BANNER_STATUS.INFO,
      bannerTitle: i18next.t('TopBanner:subscriptionOver'),
      bannerSubtitleKey: 'TopBanner:seeThePlans',
      bannerLink: ROUTES.SUBSCRIPTION_PLANS.path
    },
    [TOP_BANNER_STATUS.SUBSCRIPTION_PAUSED]: {
      bannerStatus: BANNER_STATUS.ALERT,
      bannerTitle: i18next.t('TopBanner:subscriptionPaused'),
      bannerSubtitleKey: 'TopBanner:checkSubscriptionStatus',
      bannerLink: ROUTES.PROFILE.path
    }
  }[status]);

// Los sistemas caídos se muestran con nombre propio (PJN, MEV, EJE) para que el usuario sepa
// exactamente qué gestiones están pausadas.
const getSystemsDownProps = downSystems => {
  const systems = formatSystemsList(downSystems);
  const allSystemsDown = downSystems.length === MONITORED_SYSTEMS.length;

  return {
    bannerStatus: BANNER_STATUS.WARNING,
    bannerTitle: i18next.t(downSystems.length > 1 ? 'TopBanner:systemsDown' : 'TopBanner:systemDown', {
      systems
    }),
    bannerSubtitleKey: allSystemsDown ? 'TopBanner:allSystemsPaused' : 'TopBanner:systemPaused',
    bannerSubtitleValues: { systems }
  };
};

// hideSubscriptionBanner: screens with their own subscription blocking (PremiumFeatureGate, SubscriptionGate) only
// show system alerts
export const useTopBanner = ({ currentWorkspace, scraperHealth, hideSubscriptionBanner }) => {
  const status = useSubscriptionStatus(currentWorkspace);
  const daysRemaining = useMemo(() =>
    getNumberOfDaysBetween(currentDate(), getFreeTrialEndDate(currentWorkspace), [currentWorkspace])
  );

  const downSystems = useMemo(() => {
    if (scraperHealth?.downSystems) {
      return MONITORED_SYSTEMS.filter(system => scraperHealth.downSystems.includes(system));
    }
    return MONITORED_SYSTEMS.filter(system => scraperHealth?.systems?.[system]?.isUp === false);
  }, [scraperHealth]);

  if (downSystems.length) {
    return getSystemsDownProps(downSystems);
  }
  return hideSubscriptionBanner
    ? getStatusProps(TOP_BANNER_STATUS.NORMAL)
    : getStatusProps(status, daysRemaining);
};
